<template>
  <NebulaSidebar
    class="editor-page-drawer-sidebar"
    :class="{ 'editor-page-drawer-sidebar--expanded-mobile': isFullyExpanded && isMobileSized }"
    :de-tracking-name="trackingName"
    ref="nebulaSidebar"
    @sidebar-collapsed="getSidebarCollapsed($event)"
  >
    <template #sidebar-content>
      <EditorPageDrawer
        :is-disabled="isSidebarCollapsed"
        :header-is-fixed="!isMobileSized"
        :aria-hidden="isSidebarCollapsed ? 'true' : 'false'"
      >
        <template
          v-if="lessonName || isAssignedToStudent"
          #header
        >
          <NebulaBadge
            v-if="lessonName"
            :text="lessonName"
            size="s"
            class="editor-page-drawer-sidebar__lesson-number-badge"
          />
          <NebulaBadge
            v-if="isAssignedToStudent"
            :text="$t('Assigned')"
            size="s"
            class="editor-page-drawer-sidebar__assigned-badge"
          />
        </template>
      </EditorPageDrawer>
    </template>
  </NebulaSidebar>
</template>

<script>
import { mapState } from 'pinia';
import {
  NebulaSidebar,
  NebulaBadge,
} from '@discoveryedu/nebula-components';
import { isNil, throttle } from 'lodash-es';
import EditorPageDrawer from '@/components/EditorPageDrawer.vue';
import { useUserStore } from '@/stores';
import { isIframe } from '@/lib/utils';

export default {
  name: 'EditorPageDrawerSidebar',
  components: {
    EditorPageDrawer,
    NebulaBadge,
    NebulaSidebar,
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      isSidebarCollapsed: false,
      isFullyExpanded: true,
      expandingTimeout: null,
    };
  },
  props: {
    lessonName: {
      type: String,
      default: null,
    },
    isAssigned: {
      type: Boolean,
      default: null,
    },
  },
  created() {
    this.handleResizeThrottled = throttle(this.handleResize, 100);
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.handleResizeThrottled);

      if (isIframe() && this.userIsStudent) {
        this.collapseSidebar();
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResizeThrottled);
    clearTimeout(this.expandingTimeout);
  },
  methods: {
    getSidebarCollapsed(isCollapsed) {
      this.isSidebarCollapsed = isCollapsed;
    },
    collapseSidebar() {
      const sideBar = this.$refs.nebulaSidebar;
      if (!sideBar.sidebarCollapsed) sideBar.handleCollapse();
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },
  computed: {
    ...mapState(useUserStore, [
      'userIsStudent',
    ]),
    isMobileSized() {
      return this.windowWidth <= 600;
    },
    isAssignedToStudent() {
      return isNil(this.isAssigned)
        ? this.userIsStudent && this.$route.query.homework_id
        : this.isAssigned;
    },
    trackingName() {
      return this.isFullyExpanded
        ? 'studio_lesson-sidebar_sidebar-toggle-button_open'
        : 'studio_lesson-sidebar_sidebar-toggle-button_close';
    },
  },
  watch: {
    isSidebarCollapsed(newValue) {
      clearTimeout(this.expandingTimeout);
      if (newValue) {
        this.isFullyExpanded = false;
      } else {
        this.expandingTimeout = setTimeout(() => {
          this.isFullyExpanded = true;
        }, 350);
      }
    },
  },
};
</script>

<style lang="stylus">
.editor-page-drawer-sidebar {
  background: $nebula-color-white;
  box-shadow: $nebula-shadow-400;
  height: 100%;
  padding: 0;
  position: relative;
  width: 240px;
  z-index: 1; // Above editor stage area

  &--expanded-mobile {
    position: absolute;
    z-index: 3;

    &:before {
      background-color: $nebula-color-white;
      content: '';
      height: 100%;
      position: absolute;
      width: 100%;
      z-index: -1;
    }

    &:after {
      background-color: $nebula-color-interface-blue-500;
      content: '';
      height: 100%;;
      left: 0;
      opacity: 0.8;
      position: absolute;
      top: 0;
      width: 100vw;
      z-index: -2;
    }
  }

  &__content {
    flex: 1 1 auto;
  }

  &__lesson-number-badge {
    --nebula-badge-background-color: $nebula-color-science-primary-500;
  }

  &__assigned-badge {
    --nebula-badge-background-color: $nebula-color-platform-neutral-800;
  }

  &.nebula-sidebar--collapsed {
    margin-inline-start: -215px;
  }

  .nebula-sidebar__inner {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .nebula-sidebar__toggle-nav-button {
    z-index: 3; // Above page drawer sticky headers
  }

  .editor-page-drawer__header {
    background-color: $nebula-color-white;
    border-bottom: 1px solid $nebula-color-platform-interface-300;
    display: flex;
    flex: 0 0 auto;
    gap: $nebula-space-1x;
    padding: $nebula-space-1x $nebula-space-2x;

    &--fixed {
      padding: $nebula-space-2x;
    }
  }
}
</style>
